import { pluralize } from '../../i18n/pluralize';
import translations from './wikiItem.translations';
import { wikiProse } from './wikiTypography';
import { skinCount, wikiHref, type WikiT } from './wikiText';
import type { WikiCrumb } from './wikiCrumbs';

import type {
  WikiAgentSide,
  WikiCollectibleKind,
  WikiContainerKind,
  WikiItemDetails,
  WikiItemType,
  WikiItemVariant,
  WikiStickerEffect,
  WikiStickerKind,
} from './wikiItem.types';
import type { Language } from '../../i18n/types';

export type WikiItemTextKey = keyof typeof translations.en;

export type WikiItemT = (key: WikiItemTextKey, params?: Record<string, string | number>) => string;

export const wikiItemText = (lang: Language): WikiItemT => {
  const dictionary = translations[lang];
  return (key, params) =>
    wikiProse(params ? dictionary[key].replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(params[name] ?? '')) : dictionary[key]);
};

const ITEM_SEGMENT: Record<WikiItemType, string> = {
  sticker: 'stickers',
  agent: 'agents',
  charm: 'charms',
  music_kit: 'music-kits',
  patch: 'patches',
  graffiti: 'graffiti',
  highlight: 'highlights',
  pin: 'pins',
  pass: 'passes',
  collectible: 'collectibles',
  key: 'keys',
};

export const WIKI_ITEM_TYPES: WikiItemType[] = ['sticker', 'agent', 'charm', 'music_kit', 'patch', 'graffiti', 'highlight', 'pin', 'pass', 'collectible', 'key'];

export const wikiItemListPath = (type: WikiItemType): string => `/wiki/${ITEM_SEGMENT[type]}`;

export const wikiItemPath = (type: WikiItemType, slug: string): string => `${wikiItemListPath(type)}/${slug}`;

// WHY: /wiki/cases/<что-то> занят страницами кейсов — вид контейнера идёт параметром; адрес собирается только здесь, чтобы сменить схему в одном месте
export const wikiContainersPath = (kind: WikiContainerKind | null): string => (kind ? `/wiki/cases?kind=${kind}` : '/wiki/cases');

export const WIKI_CONTAINER_KINDS: WikiContainerKind[] = [
  'case',
  'sticker_capsule',
  'autograph_capsule',
  'souvenir_package',
  'music_kit_box',
  'patch_capsule',
  'pins_capsule',
  'graffiti_box',
  'other',
];

export const isWikiContainerKind = (value: string | undefined): value is WikiContainerKind =>
  WIKI_CONTAINER_KINDS.some((kind) => kind === value);

const TYPE_KEY: Record<WikiItemType, { one: WikiItemTextKey; section: WikiItemTextKey; h1: WikiItemTextKey }> = {
  sticker: { one: 'typeSticker', section: 'sectionStickers', h1: 'listH1Stickers' },
  agent: { one: 'typeAgent', section: 'sectionAgents', h1: 'listH1Agents' },
  charm: { one: 'typeCharm', section: 'sectionCharms', h1: 'listH1Charms' },
  music_kit: { one: 'typeMusicKit', section: 'sectionMusicKits', h1: 'listH1MusicKits' },
  patch: { one: 'typePatch', section: 'sectionPatches', h1: 'listH1Patches' },
  graffiti: { one: 'typeGraffiti', section: 'sectionGraffiti', h1: 'listH1Graffiti' },
  highlight: { one: 'typeHighlight', section: 'sectionHighlights', h1: 'listH1Highlights' },
  pin: { one: 'typePin', section: 'sectionPins', h1: 'listH1Pins' },
  pass: { one: 'typePass', section: 'sectionPasses', h1: 'listH1Passes' },
  collectible: { one: 'typeCollectible', section: 'sectionCollectibles', h1: 'listH1Collectibles' },
  key: { one: 'typeKey', section: 'sectionKeys', h1: 'listH1Keys' },
};

export const itemTypeName = (ti: WikiItemT, type: WikiItemType): string => ti(TYPE_KEY[type].one);

export const itemSectionName = (ti: WikiItemT, type: WikiItemType): string => ti(TYPE_KEY[type].section);

export const itemListH1 = (ti: WikiItemT, type: WikiItemType): string => ti(TYPE_KEY[type].h1);

const KIND_KEY: Record<WikiContainerKind, { one: WikiItemTextKey; tab: WikiItemTextKey; h1: WikiItemTextKey | null }> = {
  case: { one: 'kindCase', tab: 'tabCases', h1: null },
  sticker_capsule: { one: 'kindStickerCapsule', tab: 'tabStickerCapsules', h1: 'h1StickerCapsules' },
  autograph_capsule: { one: 'kindAutographCapsule', tab: 'tabAutographCapsules', h1: 'h1AutographCapsules' },
  souvenir_package: { one: 'kindSouvenirPackage', tab: 'tabSouvenirPackages', h1: 'h1SouvenirPackages' },
  music_kit_box: { one: 'kindMusicKitBox', tab: 'tabMusicKitBoxes', h1: 'h1MusicKitBoxes' },
  patch_capsule: { one: 'kindPatchCapsule', tab: 'tabPatchCapsules', h1: 'h1PatchCapsules' },
  pins_capsule: { one: 'kindPinsCapsule', tab: 'tabPinsCapsules', h1: 'h1PinsCapsules' },
  graffiti_box: { one: 'kindGraffitiBox', tab: 'tabGraffitiBoxes', h1: 'h1GraffitiBoxes' },
  other: { one: 'kindOther', tab: 'tabOther', h1: 'h1Other' },
};

export const containerKindName = (ti: WikiItemT, kind: WikiContainerKind): string => ti(KIND_KEY[kind].one);

export const containerKindTab = (ti: WikiItemT, kind: WikiContainerKind): string => ti(KIND_KEY[kind].tab);

export const containersH1 = (t: WikiT, ti: WikiItemT, kind: WikiContainerKind | null): string => {
  if (kind === null) return ti('containersH1');
  const key = KIND_KEY[kind].h1;
  return key ? ti(key) : t('casesH1');
};

type CountKeys = [WikiItemTextKey, WikiItemTextKey, WikiItemTextKey];

const COUNT_KEY: Record<Exclude<WikiContainerKind, 'case' | 'souvenir_package'>, CountKeys> = {
  sticker_capsule: ['stickersOne', 'stickersFew', 'stickersMany'],
  autograph_capsule: ['stickersOne', 'stickersFew', 'stickersMany'],
  music_kit_box: ['musicKitsOne', 'musicKitsFew', 'musicKitsMany'],
  patch_capsule: ['patchesOne', 'patchesFew', 'patchesMany'],
  pins_capsule: ['pinsOne', 'pinsFew', 'pinsMany'],
  graffiti_box: ['graffitiOne', 'graffitiFew', 'graffitiMany'],
  other: ['itemsOne', 'itemsFew', 'itemsMany'],
};

const countOf = (ti: WikiItemT, lang: Language, [one, few, many]: CountKeys, count: number): string =>
  pluralize(count, { one: ti(one, { count }), few: ti(few, { count }), many: ti(many, { count }) }, lang);

// WHY: в кейсе и сувенирном наборе — оружейные скины, в остальных контейнерах счёт идёт своими словами
export const containerItemCount = (t: WikiT, ti: WikiItemT, lang: Language, kind: WikiContainerKind, count: number): string =>
  kind === 'case' || kind === 'souvenir_package' ? skinCount(t, lang, count) : countOf(ti, lang, COUNT_KEY[kind], count);

export const colorCount = (ti: WikiItemT, lang: Language, count: number): string =>
  countOf(ti, lang, ['colorsOne', 'colorsFew', 'colorsMany'], count);

export const WIKI_STICKER_EFFECTS: WikiStickerEffect[] = ['paper', 'glitter', 'holo', 'foil', 'gold', 'lenticular', 'embroidered'];

const EFFECT_KEY: Record<WikiStickerEffect, WikiItemTextKey> = {
  paper: 'effectPaper',
  holo: 'effectHolo',
  foil: 'effectFoil',
  gold: 'effectGold',
  glitter: 'effectGlitter',
  lenticular: 'effectLenticular',
  embroidered: 'effectEmbroidered',
};

export const effectName = (ti: WikiItemT, effect: WikiStickerEffect): string => ti(EFFECT_KEY[effect]);

const STICKER_KIND_KEY: Record<WikiStickerKind, WikiItemTextKey> = {
  autograph: 'stickerAutograph',
  team: 'stickerTeam',
  event: 'stickerEvent',
  other: 'stickerOther',
};

export const stickerKindName = (ti: WikiItemT, kind: WikiStickerKind): string => ti(STICKER_KIND_KEY[kind]);

export const agentSideName = (ti: WikiItemT, side: WikiAgentSide): string => ti(side === 't' ? 'sideT' : 'sideCt');

const COLLECTIBLE_KIND_KEY: Record<WikiCollectibleKind, WikiItemTextKey> = {
  coin: 'collectibleCoin',
  trophy: 'collectibleTrophy',
  medal: 'collectibleMedal',
  other: 'typeCollectible',
};

export const collectibleKindName = (ti: WikiItemT, kind: WikiCollectibleKind): string => ti(COLLECTIBLE_KIND_KEY[kind]);

const comparable = (text: string): string => text.replace(/ /g, ' ').toLocaleLowerCase();

// WHY: у наклейки, нашивки, значка и медали тип уже стоит в начале официального названия («Нашивка | …») — чип с тем же словом повторял бы заголовок
export const nameStartsWithKind = (name: string, kind: string): boolean => comparable(name).startsWith(comparable(kind));

// WHY: монета, медаль и трофей живут в одном разделе — у страницы подпись по виду, а не общее «Награда»
export const itemKindName = (ti: WikiItemT, item: WikiItemDetails): string =>
  item.type === 'collectible' ? collectibleKindName(ti, item.collectible.kind) : itemTypeName(ti, item.type);

// WHY: подпись варианта собирается из его признаков — эффект и слэб переводит словарь, цвет граффити приходит готовым названием из игры
export const variantLabel = (t: WikiT, ti: WikiItemT, variant: Pick<WikiItemVariant, 'tint' | 'effect' | 'stattrak'>): string => {
  if (variant.tint) return variant.tint.name;
  if (variant.effect) return effectName(ti, variant.effect);
  return t(variant.stattrak ? 'kindStatTrak' : 'kindNormal');
};

export const wikiItemCrumbs = (lang: Language, t: WikiT, ti: WikiItemT, type: WikiItemType, name: string): WikiCrumb[] => [
  { label: t('wiki'), href: wikiHref(lang, '/wiki') },
  { label: itemSectionName(ti, type), href: wikiHref(lang, wikiItemListPath(type)) },
  { label: name },
];

export const wikiContainerCrumbs = (lang: Language, t: WikiT, ti: WikiItemT, kind: WikiContainerKind | undefined, name: string): WikiCrumb[] => [
  { label: t('wiki'), href: wikiHref(lang, '/wiki') },
  kind === undefined || kind === 'case'
    ? { label: t('casesH1'), href: wikiHref(lang, '/wiki/cases') }
    : { label: containerKindTab(ti, kind), href: wikiHref(lang, wikiContainersPath(kind)) },
  { label: name },
];

// WHY: в капсуле у всех наклеек одна приставка «Наклейка |» — на карточке она лишняя; у агента первая часть — имя, её не трогаем
export const itemCardName = (type: WikiItemType, name: string): string => {
  if (type === 'agent') return name;
  const at = name.indexOf(' | ');
  return at === -1 ? name : name.slice(at + 3);
};
